import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    toggleEditModal,
    setEditedOrganizer,
    toggleNeedRefresh,
} from '../../../store/organizersSlice';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as ChartIcon } from '../../icons/chart.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as LockIcon } from '../../icons/lock.svg';
import { ReactComponent as UnlockIcon } from '../../icons/unlock.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import ApiService from '../../api_services/apiService.jsx';
import { normalizeOrganizerProfile, isOrganizerPending, resolveOrganizerId } from '../../../tables/organizers/organizersTableService.js';
import './OrganizerEditPanel.css';

function OrganizerEditPanel({ allOrganizers = [] }) {
    const dispatch = useDispatch();
    const editModalOpen = useSelector((state) => state.organizers.editModalOpen);
    const editedOrganizer = useSelector((state) => state.organizers.editedOrganizer);
    const editedOrganizerId = resolveOrganizerId(editedOrganizer);
    const isPendingOrganizer = isOrganizerPending(editedOrganizer);

    useEffect(() => {
        let isMounted = true;

        const fetchOrganizerProfile = async () => {
            if (!editedOrganizerId) return;
            try {
                const response = await ApiService.getOrganiserProfile(editedOrganizerId);
                const profile = normalizeOrganizerProfile(response?.data ?? response);
                if (isMounted && profile?.id) {
                    dispatch(setEditedOrganizer(profile));
                }
            } catch (error) {
                console.error('Error fetching organizer profile:', error);
            }
        };

        fetchOrganizerProfile();

        return () => {
            isMounted = false;
        };
    }, [editedOrganizerId, dispatch]);
    
    // Tab state: 'basic' or 'description'
    const [activeTab, setActiveTab] = useState('basic');
    
    // Form state
    const [formData, setFormData] = useState({
        companyName: '',
        nip: '',
        email: '',
        phone: '',
        street: '',
        zipCode: '',
        city: '',
        description: '',
        facebookUrl: '',
        websiteUrl: '',
    });
    
    // Track which fields have been changed (for pending organizers)
    const [changedFields, setChangedFields] = useState({});
    
    // Initialize form data when organizer changes
    useEffect(() => {
        if (editedOrganizer) {
            const addressParts = getAddressParts(editedOrganizer.address);
            setFormData({
                companyName: editedOrganizer.companyName || '',
                nip: editedOrganizer.nip || '',
                email: editedOrganizer.email || '',
                phone: editedOrganizer.phone || '',
                street: addressParts.street || '',
                zipCode: addressParts.zipCode || '',
                city: addressParts.city || '',
                description: editedOrganizer.description || '',
                facebookUrl: editedOrganizer.facebookUrl || '',
                websiteUrl: editedOrganizer.websiteUrl || '',
            });
            
            // For pending organizers with data change, mark the changed fields
            if (editedOrganizer.pendingType === 'data_change' && editedOrganizer.changedFields) {
                setChangedFields(editedOrganizer.changedFields);
            } else {
                setChangedFields({});
            }
        }
    }, [editedOrganizer]);
    
    // Find current index in all organizers
    const currentIndex = allOrganizers.findIndex(org => org.id === editedOrganizer?.id);
    const totalCount = allOrganizers.length;
    
    // Navigation handlers
    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            dispatch(setEditedOrganizer(allOrganizers[currentIndex - 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);
    
    const handleNext = useCallback(() => {
        if (currentIndex < allOrganizers.length - 1) {
            dispatch(setEditedOrganizer(allOrganizers[currentIndex + 1]));
        }
    }, [currentIndex, allOrganizers, dispatch]);
    
    // Close handler
    const handleClose = useCallback(() => {
        dispatch(toggleEditModal());
        dispatch(setEditedOrganizer(null));
    }, [dispatch]);
    
    // Form change handler
    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);
    
    // Save handler
    const handleSave = useCallback(() => {
        console.log('Save organizer data:', formData);
        // TODO: Implement API call
        dispatch(toggleNeedRefresh());
        handleClose();
    }, [formData, dispatch, handleClose]);

    const handleApproveOrganizer = useCallback(async () => {
        if (!editedOrganizer || !isOrganizerPending(editedOrganizer)) {
            return;
        }

        try {
            if (!editedOrganizerId) return;
            await ApiService.approveOrganiser(editedOrganizerId);
            const response = await ApiService.getOrganiserProfile(editedOrganizerId);
            const profile = normalizeOrganizerProfile(response?.data ?? response);
            dispatch(setEditedOrganizer(profile));
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('Error approving organizer:', error);
        }
    }, [editedOrganizer, editedOrganizerId, dispatch]);
    
    // Password reset handler
    const handlePasswordReset = useCallback(() => {
        if (editedOrganizer) {
            if (window.confirm(`Czy na pewno chcesz wysłać wiadomość z resetem hasła do "${formData.email}"?`)) {
                ApiService.requestPasswordRecovery(formData.email)
                    .then(() => {
                        alert('Wysłano wiadomość z resetem hasła.');
                    })
                    .catch((error) => {
                        console.error('Password reset request failed:', error);
                    });
            }
        }
    }, [editedOrganizer, formData.email]);
    
    // Statistics handler
    const handleStatistics = useCallback(() => {
        console.log('Show statistics for organizer:', editedOrganizer?.id);
        // TODO: Navigate to statistics page
    }, [editedOrganizer]);
    
    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/\//g, '-');
    };
    
    // Extract address parts
    const getAddressParts = (address) => {
        if (!address) return { street: '', zipCode: '', city: '' };
        
        // Try to parse address like "ul. Główna 15, 30-001 Kraków"
        const parts = address.split(',').map(p => p.trim());
        const street = parts[0] || '';
        
        if (parts[1]) {
            const cityParts = parts[1].trim().split(' ');
            const zipCode = cityParts[0] || '';
            const city = cityParts.slice(1).join(' ') || '';
            return { street, zipCode, city };
        }
        
        return { street, zipCode: '', city: '' };
    };
    
    // Check if field is changed (for pending organizers)
    const isFieldChanged = (fieldName) => {
        return changedFields[fieldName] === true;
    };
    
    if (!editModalOpen || !editedOrganizer) {
        return null;
    }
    
    return (
        <>
            {/* Backdrop */}
            <div className="organizer-edit-backdrop" onClick={handleClose} />
            
            {/* Panel */}
            <div className="organizer-edit-panel">
                {/* Header */}
                <div className="organizer-edit-header">
                    <div className="header-left">
                        <div className="navigation-arrows">
                            <button 
                                className="nav-arrow-btn"
                                onClick={handlePrevious}
                                disabled={currentIndex <= 0}
                                title="Poprzedni"
                            >
                                <ArrowLeftIcon className="nav-arrow-icon" />
                            </button>
                            <button 
                                className="nav-arrow-btn"
                                onClick={handleNext}
                                disabled={currentIndex >= totalCount - 1}
                                title="Następny"
                            >
                                <ArrowRightIcon className="nav-arrow-icon" />
                            </button>
                        </div>
                        <span className="results-counter">
                            {currentIndex + 1} z {totalCount} wyników
                        </span>
                    </div>
                    <div className="header-right">
                        <span className="registration-date">
                            Data dodania: {formatDate(editedOrganizer.registrationDate)}
                        </span>
                    </div>
                </div>
                
                {/* Main Content */}
                <div className="organizer-edit-content">
                    {/* Company Name Section */}
                    <div className="company-header-section">
                        <div className="company-info">
                            <div className="company-id-name">
                                <span className="company-id">(ID {editedOrganizer.id})</span>
                                <h2 className="company-name">{editedOrganizer.companyName}</h2>
                            </div>
                            <button 
                                className="statistics-btn disabled" 
                                onClick={handleStatistics}
                                disabled
                            >
                                <ChartIcon className="btn-icon" />
                                <span>Statystyki</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* Tabs Navigation */}
                    <div className="detail-tabs-nav">
                        <div className="tabs-container">
                            <button 
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                            >
                                Dane podstawowe
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                                onClick={() => setActiveTab('description')}
                            >
                                Opis
                            </button>
                        </div>
                    </div>
                    
                    {/* Tab Content */}
                    {activeTab === 'basic' && (
                        <div className="tab-content basic-data">
                            {/* ID Row (read-only) */}
                            <div className="data-section">
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">ID</div>
                                        <div className="data-value">{editedOrganizer.id}</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Company Info Section */}
                            <div className="data-section">
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Nazwa firmy</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.companyName}
                                                onChange={(e) => handleInputChange('companyName', e.target.value)}
                                                placeholder="Nazwa firmy"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* NIP with change badge */}
                                <div className={`data-row ${isFieldChanged('nip') ? 'has-change' : ''}`}>
                                    <div className="data-row-inner">
                                        <div className="data-label">NIP</div>
                                        <div className="data-value-input">
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={formData.nip}
                                                onChange={(e) => handleInputChange('nip', e.target.value)}
                                                placeholder="NIP"
                                            />
                                        </div>
                                    </div>
                                    {isFieldChanged('nip') && (
                                        <div className="change-badge">Nowe dane</div>
                                    )}
                                </div>
                                
                                {/* Contact row */}
                                <div className={`data-row ${isFieldChanged('email') ? 'has-change' : ''}`}>
                                    <div className="data-row-inner contact-row">
                                        <div className="data-label">Kontakt</div>
                                        <div className="data-value-input">
                                            <input
                                                type="email"
                                                className="edit-input"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                placeholder="Email"
                                            />
                                        </div>
                                        <button 
                                            className="password-reset-btn"
                                            onClick={handlePasswordReset}
                                        >
                                            Wiadomość z resetem hasła
                                        </button>
                                    </div>
                                    {isFieldChanged('email') && (
                                        <div className="change-badge">Nowe dane</div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Address Section */}
                            <div className="data-section">
                                <div className="section-header">
                                    <div className="data-row">
                                        <div className="data-row-inner">
                                            <div className="data-label">Adres</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="address-details">
                                    <div className="data-row">
                                        <div className="data-row-inner">
                                            <div className="data-label">Ulica</div>
                                            <div className="data-value-input">
                                                <input
                                                    type="text"
                                                    className="edit-input"
                                                    value={formData.street}
                                                    onChange={(e) => handleInputChange('street', e.target.value)}
                                                    placeholder="Ulica"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="data-row two-column">
                                        <div className="data-row-inner">
                                            <div className="data-label">Kod pocztowy</div>
                                            <div className="data-value-input">
                                                <input
                                                    type="text"
                                                    className="edit-input"
                                                    value={formData.zipCode}
                                                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                                                    placeholder="00-000"
                                                />
                                            </div>
                                        </div>
                                        <div className="data-row-inner">
                                            <div className="data-label">Miejscowość</div>
                                            <div className="data-value-input">
                                                <input
                                                    type="text"
                                                    className="edit-input"
                                                    value={formData.city}
                                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                                    placeholder="Miejscowość"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'description' && (
                        <div className="tab-content description-data">
                            {/* Description Section */}
                            <div className="data-section">
                                <div className="data-row description-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Opis działalności</div>
                                        <div className="data-value-input">
                                            <textarea
                                                className="edit-textarea"
                                                value={formData.description}
                                                onChange={(e) => handleInputChange('description', e.target.value)}
                                                placeholder="Opis działalności organizatora..."
                                                rows={5}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Links Section */}
                            <div className="data-section">
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Link do Facebook</div>
                                        <div className="data-value-input">
                                            <input
                                                type="url"
                                                className="edit-input"
                                                value={formData.facebookUrl}
                                                onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                                                placeholder="https://facebook.com/..."
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="data-row">
                                    <div className="data-row-inner">
                                        <div className="data-label">Strona WWW</div>
                                        <div className="data-value-input">
                                            <input
                                                type="url"
                                                className="edit-input"
                                                value={formData.websiteUrl}
                                                onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                                                placeholder="https://..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer Actions */}
                <div className="organizer-edit-footer">
                    <div className="footer-buttons">
                        {/* Left side buttons - always disabled in edit mode */}
                        <button 
                            className="action-btn outline danger disabled" 
                            disabled
                        >
                            <TrashIcon className="btn-icon" />
                            <span>Usuń</span>
                        </button>
                        <button 
                            className="action-btn outline disabled" 
                            disabled
                        >
                            {editedOrganizer.isBlocked ? (
                                <>
                                    <UnlockIcon className="btn-icon" />
                                    <span>Odblokuj</span>
                                </>
                            ) : (
                                <>
                                    <LockIcon className="btn-icon" />
                                    <span>Zablokuj</span>
                                </>
                            )}
                        </button>
                        <button 
                            className="action-btn filled disabled" 
                            disabled
                        >
                            <EditIcon className="btn-icon" />
                            <span>Edytuj</span>
                        </button>
                        
                        {/* Right side buttons - active for saving/canceling */}
                        <div className="edit-actions-wrapper">
                            {isPendingOrganizer && (
                                <button
                                    className="action-btn outline"
                                    onClick={handleApproveOrganizer}
                                    type="button"
                                >
                                    <span>Zaakceptuj</span>
                                </button>
                            )}
                            <button 
                                className="action-btn outline morski" 
                                onClick={handleClose}
                            >
                                <span>Odrzuć zmiany</span>
                            </button>
                            <button 
                                className="action-btn filled" 
                                onClick={handleSave}
                            >
                                <span>Zaakceptuj zmiany</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default OrganizerEditPanel;
